/**
 * SubjectMasteryCard
 * Shows mastery % per legal subject as a compact card grid.
 * Replaces the old FocusAreasCard weakness/strength toggle.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useSubjectMastery, SubjectMastery } from '@/hooks/use-subject-mastery';
import { slugToUuid } from '@/lib/subject-mapping';
import { Brain, ChevronDown, ChevronUp, TrendingUp, TrendingDown, BookOpen, Target, PenLine } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SubjectMasteryCardProps {
  className?: string;
}

function getMasteryColor(mastery: number) {
  if (mastery >= 70) return 'text-green-600 dark:text-green-400';
  if (mastery >= 40) return 'text-amber-600 dark:text-amber-400';
  return 'text-red-600 dark:text-red-400';
}

function getMasteryRingColor(mastery: number) {
  if (mastery >= 70) return 'stroke-green-500';
  if (mastery >= 40) return 'stroke-amber-500';
  return 'stroke-red-500';
}

function getMasteryLabel(mastery: number) {
  if (mastery >= 80) return 'Strong';
  if (mastery >= 60) return 'Good';
  if (mastery >= 40) return 'Building';
  if (mastery >= 20) return 'Developing';
  return 'Needs Work';
}

/** Compact circular mastery ring */
function MasteryRing({ mastery, size = 40 }: { mastery: number; size?: number }) {
  const strokeWidth = 3;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (mastery / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90" aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-gray-100 dark:text-gray-700"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={cn('transition-all duration-700', getMasteryRingColor(mastery))}
        />
      </svg>
      <span className={cn(
        'absolute inset-0 flex items-center justify-center text-xs font-bold',
        getMasteryColor(mastery)
      )}>
        {mastery}
      </span>
    </div>
  );
}

/** Subject tile with mastery ring and quick actions */
function SubjectTile({ subject }: { subject: SubjectMastery }) {
  const [expanded, setExpanded] = useState(false);

  const subjectUuid = slugToUuid(subject.slug);
  const flashcardParam = subjectUuid ? encodeURIComponent(subjectUuid) : encodeURIComponent(subject.slug);
  const slugParam = encodeURIComponent(subject.slug);

  return (
    <div className={cn(
      'rounded-lg border border-gray-100 dark:border-gray-700/50',
      'bg-gray-50/50 dark:bg-gray-800/30',
      'transition-all duration-200',
    )}>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        aria-label={`${subject.name}, ${subject.mastery}% mastery. ${expanded ? 'Collapse' : 'Expand'} details`}
        className="w-full flex items-center gap-3 p-3 min-h-[44px] rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F37022] focus-visible:ring-offset-1"
      >
        <MasteryRing mastery={subject.mastery} />
        <div className="flex-1 min-w-0 text-left">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {subject.name}
          </p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className={cn('text-xs', getMasteryColor(subject.mastery))}>
              {getMasteryLabel(subject.mastery)}
            </span>
            <span className="text-[11px] text-muted-foreground">
              {subject.totalEvidence} signal{subject.totalEvidence !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {subject.strengths > 0 && (
            <span className="flex items-center gap-0.5 text-[11px] text-green-600 dark:text-green-400">
              <TrendingUp className="h-3 w-3" />
              {subject.strengths}
            </span>
          )}
          {subject.weaknesses > 0 && (
            <span className="flex items-center gap-0.5 text-[11px] text-red-600 dark:text-red-400">
              <TrendingDown className="h-3 w-3" />
              {subject.weaknesses}
            </span>
          )}
          {expanded ? (
            <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-gray-100 dark:border-gray-700/50 pt-2">
          {/* Subcategory details */}
          {subject.details.length > 0 && (
            <div className="space-y-1.5">
              {subject.details.map((d, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className={cn(
                    'w-1.5 h-1.5 rounded-full shrink-0',
                    d.beliefType === 'strength' ? 'bg-green-500' : 'bg-red-500'
                  )} aria-hidden="true" />
                  <span className="text-xs text-gray-700 dark:text-gray-300 truncate flex-1">
                    <span className="sr-only">{d.beliefType === 'strength' ? 'Strength:' : 'Weakness:'}</span>
                    {formatSubcategory(d.subcategory)}
                  </span>
                  <span className="text-[11px] text-muted-foreground shrink-0">
                    {Math.round(d.confidence * 100)}%
                  </span>
                </div>
              ))}
            </div>
          )}
          {/* Quick actions */}
          <div className="flex items-center gap-1 pt-1">
            <Link
              to={`/flashcards/study?subject=${flashcardParam}`}
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline px-2 py-2 rounded hover:bg-primary/5 min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F37022] focus-visible:ring-offset-1"
            >
              <BookOpen className="h-3.5 w-3.5" />
              Study
            </Link>
            <Link
              to={`/issue-spotter?subjects=${slugParam}`}
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline px-2 py-2 rounded hover:bg-primary/5 min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F37022] focus-visible:ring-offset-1"
            >
              <Target className="h-3.5 w-3.5" />
              Spot Issues
            </Link>
            <Link
              to={`/essay-coach?subject=${slugParam}`}
              className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline px-2 py-2 rounded hover:bg-primary/5 min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F37022] focus-visible:ring-offset-1"
            >
              <PenLine className="h-3.5 w-3.5" />
              Essay
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

/** Format snake_case or kebab-case subcategory for display */
function formatSubcategory(raw: string): string {
  return raw
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

export function SubjectMasteryCard({ className }: SubjectMasteryCardProps) {
  const { data: subjects = [], isLoading } = useSubjectMastery();

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-36" />
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn(
      'bg-white dark:bg-gray-800/50',
      'border border-gray-100 dark:border-gray-700/50',
      className
    )}>
      <CardHeader className="pb-2 pt-4">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
          <Brain className="h-4 w-4 text-[#F37022]" />
          Subject Mastery
        </CardTitle>
      </CardHeader>
      <CardContent className="pb-4">
        {subjects.length > 0 ? (
          <div className="space-y-2">
            {subjects.map((subject) => (
              <SubjectTile key={subject.slug} subject={subject} />
            ))}
          </div>
        ) : (
          <div className="flex items-center gap-4 py-2">
            <div className="shrink-0 w-10 h-10 relative">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30" />
              <div className="absolute inset-0.5 rounded-full bg-white dark:bg-gray-800 flex items-center justify-center">
                <Brain className="h-4 w-4 text-[#F37022]" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                Building your mastery profile
              </p>
              <p className="text-xs text-muted-foreground">
                Practice with flashcards, essays, or issue spotting to see your progress here
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default SubjectMasteryCard;
